import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Filter, Loader2, Search, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { DatePicker } from "@/components/common/DatePicker";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EXPENSE_CATS } from "@/components/finance/ExpensesCard";
import { api, formatApiError } from "@/lib/api";
import { useFormat } from "@/lib/format";

const SOURCES = { all: "Toutes les sources", bank: "Opérations bancaires", expense: "Dépenses saisies", household: "Dépenses du foyer" };
const EMPTY = { q: "", category: "all", source: "all", date_from: "", date_to: "", min_amount: "", max_amount: "", fees_only: false };

const Filters = ({ f, setF }) => {
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  return (
    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4" data-testid="ops-filters">
      <div className="relative sm:col-span-2"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={f.q} onChange={set("q")} placeholder="Libellé, fournisseur…" className="pl-9" data-testid="ops-filter-q" /></div>
      <Select value={f.category} onValueChange={set("category")}><SelectTrigger data-testid="ops-filter-category"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="all">Toutes les catégories</SelectItem>{Object.entries(EXPENSE_CATS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
      <Select value={f.source} onValueChange={set("source")}><SelectTrigger data-testid="ops-filter-source"><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(SOURCES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
      <DatePicker value={f.date_from} onChange={set("date_from")} noFuture placeholder="Du" data-testid="ops-filter-from" />
      <DatePicker value={f.date_to} onChange={set("date_to")} noFuture placeholder="Au" data-testid="ops-filter-to" />
      <Input type="number" min="0" step="0.01" value={f.min_amount} onChange={set("min_amount")} placeholder="Montant min €" data-testid="ops-filter-min" />
      <Input type="number" min="0" step="0.01" value={f.max_amount} onChange={set("max_amount")} placeholder="Montant max €" data-testid="ops-filter-max" />
      <label className="flex items-center gap-2 text-sm sm:col-span-2"><Checkbox checked={f.fees_only} onCheckedChange={(v) => setF({ ...f, fees_only: !!v })} data-testid="ops-filter-fees" /> Frais bancaires uniquement</label>
      <Button variant="ghost" className="rounded-full sm:col-span-2 lg:justify-self-end" onClick={() => setF(EMPTY)} data-testid="ops-filter-reset">Réinitialiser les filtres</Button>
    </div>
  );
};

const OpRow = ({ o, checked, onToggle }) => {
  const { money, date } = useFormat();
  return (
    <li className="flex items-center gap-3 py-2 text-sm" data-testid={`ops-row-${o.id}`}>
      <Checkbox checked={checked} onCheckedChange={() => onToggle(o.id)} aria-label={`Sélectionner ${o.label}`} data-testid={`ops-select-${o.id}`} />
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">{o.label}</div>
        <div className="truncate text-xs text-muted-foreground">{date(o.date)} · {o.category_label} · {o.account}</div>
      </div>
      {o.shared && <Badge variant="secondary" className="shrink-0 rounded-full text-[10px]" data-testid={`ops-shared-${o.id}`}>partagé</Badge>}
      {o.fee_kind && <Badge variant="outline" className="hidden rounded-full text-[10px] sm:inline-flex">Frais</Badge>}
      <span className={`num shrink-0 ${o.amount < 0 ? "text-success" : ""}`}>{o.amount === null ? "•••" : money(-o.amount, { maximumFractionDigits: 2 })}</span>
    </li>
  );
};

export const OperationsExplorer = ({ onReport }) => {
  const qc = useQueryClient();
  const { money } = useFormat();
  const [f, setF] = useState(EMPTY);
  const [sel, setSel] = useState([]);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const params = Object.fromEntries(Object.entries(f).filter(([k, v]) => v !== "" && v !== "all" && !(k === "fees_only" && !v)));
  const { data, isLoading } = useQuery({ queryKey: ["operations", params], queryFn: () => api.get("/finance/operations", { params }).then((r) => r.data) });
  const items = data?.items || [];
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length >= 40 ? s : [...s, id]));
  const allOn = items.length > 0 && items.slice(0, 40).every((o) => sel.includes(o.id));
  const evaluate = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/finance/reports/evaluate", { ids: sel, title: title.trim() || null });
      qc.invalidateQueries({ queryKey: ["reports"] });
      setSel([]); setTitle("");
      onReport(r);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <section className="card-surface space-y-5 p-5 sm:p-6" data-testid="ops-explorer">
      <div className="flex items-center gap-3">
        <span className="tone-chip tone-sky flex h-9 w-9 items-center justify-center rounded-xl"><Filter className="h-4 w-4" /></span>
        <div><h2 className="text-lg font-bold">Filtrer et évaluer des opérations</h2>
          <p className="text-xs text-muted-foreground">Sélectionnez une ou plusieurs dépenses : l'assistant IA les compare à votre historique et à vos charges récurrentes (lecture seule).</p></div>
      </div>
      <Filters f={f} setF={setF} />
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <label className="flex items-center gap-2"><Checkbox checked={allOn} onCheckedChange={() => setSel(allOn ? [] : items.slice(0, 40).map((o) => o.id))} data-testid="ops-select-all" /> Tout sélectionner</label>
        <span className="text-muted-foreground" data-testid="ops-count">{data ? `${data.count} opération(s) · ${money(data.total_debit, { maximumFractionDigits: 0 })} de dépenses` : ""}</span>
      </div>
      {isLoading ? <div className="h-40 animate-pulse rounded-2xl bg-muted" /> : items.length ? (
        <ul className="max-h-[28rem] divide-y divide-border overflow-y-auto pr-1" data-testid="ops-list">
          {items.map((o) => <OpRow key={o.id} o={o} checked={sel.includes(o.id)} onToggle={toggle} />)}
        </ul>
      ) : <p className="text-sm text-muted-foreground" data-testid="ops-empty">Aucune opération ne correspond aux filtres.</p>}
      <div className="sticky bottom-20 z-10 flex flex-col gap-2 rounded-2xl border border-border bg-background/95 p-3 backdrop-blur sm:flex-row sm:items-center lg:bottom-4">
        <span className="text-sm font-semibold" data-testid="ops-selected-count">{sel.length} sélectionnée(s){sel.length >= 40 && " (max. 40)"}</span>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titre du rapport (facultatif)" className="sm:flex-1" maxLength={100} data-testid="ops-report-title" />
        <Button className="rounded-full" disabled={!sel.length || busy} onClick={evaluate} data-testid="ops-evaluate-button">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}{busy ? "Analyse en cours…" : "Évaluer avec l'IA"}
        </Button>
      </div>
    </section>
  );
};
